import { gql } from '@apollo/client'

export const BOOKING_DETAILS_QUERY = gql`
  query BookingDetail($reservationNumber: String!) {
    bookingDetail(reservationNumber: $reservationNumber) {
      basicDetails {
        id
        reservationNumber
        displayReservationNumber
        accommodationName
        accommodationNameTranslatedList {
          nativeLabel
          label
          value
        }
        createdAt
        expiredAt
        crsStatus
        pmsStatus
        reservationType
        ITTICReservationType
        currency
        numberOfRooms
        numberOfGuest
        propertyTelephone
        propertyWebsite
        propertyAddress
        propertyAddressFA
        propertyCode
        displayPropertyCode
        providerCode
        displayProviderCode
        providerName
        walletType
        tenantCode
        displayTenantCode
      }
      bookerInfo {
        firstName
        lastName
        fullName
        idNumber
        email
        telephone
        isForeigner
      }
      canCancel
      canModify
      canConfirm
      lastChanger {
        name
        email
        ip
      }
      sourceInfo {
        name
        channelCode
        displayChannelCode
        Id
        reservationNumber
        displayReservationNumber
        shortName
        paymentNumber
      }
      rooms {
        crsStatus
        pmsStatus
        canCancel
        canModify
        reservationNumber
        displayReservationNumber
        pmsNumber
        pmsSecondaryNumber
        channelName
        channelID
        numberOfAdults
        addBed
        occupancy {
          adults
          extraBeds
        }
        cancellationPolicy {
          name
          description
          descriptionTranslatedList {
            label
            nativeLabel
            value
          }
          policies {
            applied
            priority
            penaltyAmount
            deadline
            deadlineTimeOffset
            penaltyType
            policyType
          }
        }
        modificationPolicy {
          name
          description
          descriptionTranslatedList {
            label
            nativeLabel
            value
          }
          policies {
            applied
            priority
            penaltyAmount
            deadline
            deadlineTimeOffset
            penaltyType
            policyType
          }
        }
        ratePolicy
        ratePolicyTranslatedList {
          label
          nativeLabel
          value
        }
        checkIn
        checkOut
        payablePrice
        providerName
        originalPrice
        discount
        addones
        additionalDiscount
        promotionDiscount
        cancelationPenalty
        changePenalty
        modificationDiff
        extraBedPrice
        boardType
        profiles {
          fullName
          firstName
          lastName
          isForeigner
          email
          mobile
          phone
          identificationNo
          id
        }
        roomTypeName
        roomTypeCode
        displayRoomTypeCode
        failedReason
        failureReasonCode
        technicalFailure {
          categoryCode
          providerCode
          httpStatus
          message
        }
        ratePlanName
        ratePlanCode
        displayRatePlanCode
        ratesPerDay {
          date
          roomRate
          extraBedRate
        }
      }
      description
      roomsPriceDetails {
        roomTypeCode
        displayRoomTypeCode
        roomTypeName
        displayRatePlanCode
        chargePerDays {
          date
          charge {
            itemName
            quantity
            discount
            rate
            total
            remark
          }
        }
        chargePerRoomStay {
          itemName
          quantity
          discount
          rate
          total
          remark
        }
        totalChargePerRoomStay
        totalChargePerDay
      }
      billingSummary {
        roomsPrice {
          quantity
          price
          roomTypeName
          ratePlanCode
          displayRatePlanCode
        }
        subTotalBeforeDiscount
        subTotalAfterDiscount
        discount
        additionalDiscount
        promotionDiscount
        taxAndFees
        amendmentPenaltyAmount
        cancelationPenaltyAmount
        refundAmount
        reservationMethod
        curruncy
        finalAmount
      }
      # The AMOUNT is billingSummary.promotionDiscount above; this is the identity —
      # which promotion produced it. Requested here so the details screen can name the
      # discount rather than only totalling it.
      promotionApplication {
        applied {
          promotionId
          name
          # The amount and its attribution, not just the identity. Without these the
          # screen can name the discount but not say what it was worth or which room
          # stay it landed on — which wbs-ng has shown a booker all along.
          discountMinor
          currency
          distribution
          allocations {
            roomStayReference
            discountMinor
            currency
          }
        }
        totalDiscountMinor
        currency
        state
      }
      failedReason
    }
  }
`
export const BOOKING_DETAILS_FOR_ROOM_STAY_QUERY = gql`
  query BookingDetailForRoomStay(
    $reservationNumber: String!
    $roomReservationNumber: String!
  ) {
    bookingDetailForRoomStay(
      reservationNumber: $reservationNumber
      roomReservationNumber: $roomReservationNumber
    ) {
      basicDetails {
        reservationNumber
        expiredAt
        currency
        canConfirm
      }
      room {
        crsStatus
        pmsStatus
        canCancel
        canModify
        reservationNumber
        displayReservationNumber
        pmsNumber
        pmsSecondaryNumber
        channelName
        channelID
        numberOfAdults
        addBed
        occupancy {
          adults
          extraBeds
        }
        cancellationPolicy {
          name
          description
          descriptionTranslatedList {
            label
            nativeLabel
            value
          }
          policies {
            applied
            priority
            penaltyAmount
            deadline
            deadlineTimeOffset
            penaltyType
            policyType
          }
        }
        modificationPolicy {
          name
          description
          descriptionTranslatedList {
            label
            nativeLabel
            value
          }
          policies {
            applied
            priority
            penaltyAmount
            deadline
            deadlineTimeOffset
            penaltyType
            policyType
          }
        }
        ratePolicy
        ratePolicyTranslatedList {
          label
          nativeLabel
          value
        }
        checkIn
        checkOut
        payablePrice
        providerName
        originalPrice
        discount
        addones
        additionalDiscount
        promotionDiscount
        cancelationPenalty
        changePenalty
        modificationDiff
        extraBedPrice
        boardType
        profiles {
          fullName
          firstName
          lastName
          isForeigner
          email
          mobile
          phone
          identificationNo
        }
        roomTypeName
        roomTypeCode
        displayRoomTypeCode
        failedReason
        ratePlanName
        failureReasonCode
        technicalFailure {
          categoryCode
          providerCode
          httpStatus
          message
        }
        ratePlanCode
        displayRatePlanCode
        ratesPerDay {
          date
          roomRate
          extraBedRate
        }
      }
    }
  }
`
export const BOOKING_HISTORY_QUERY = gql`
  query BookingHistory(
    $reservationNumber: String!
    $page: Int!
    $perPage: Int!
  ) {
    bookingHistory(
      reservationNumber: $reservationNumber
      page: $page
      perPage: $perPage
    ) {
      bookingHistories {
        basicDetails {
          id
          reservationNumber
          displayReservationNumber
          accommodationName
          accommodationNameTranslatedList {
            nativeLabel
            label
            value
          }
          createdAt
          expiredAt
          crsStatus
          pmsStatus
          reservationType
          currency
          numberOfRooms
          numberOfGuest
          propertyTelephone
          propertyWebsite
          propertyAddress
          propertyAddressFA
          propertyCode
          displayPropertyCode
          tenantCode
          displayTenantCode
          version
        }
        bookerInfo {
          firstName
          lastName
          fullName
          idNumber
          email
          telephone
          isForeigner
        }
        canCancel
        canModify
        canConfirm
        canConfirm
        lastChanger {
          name
          email
          ip
        }
        sourceInfo {
          name
          channelCode
          displayChannelCode
          reservationNumber
          displayReservationNumber
          shortName
          paymentNumber
        }
        rooms {
          crsStatus
          pmsStatus
          canCancel
          canModify
          reservationNumber
          displayReservationNumber
          pmsNumber
          pmsSecondaryNumber
          channelName
          channelID
          numberOfAdults
          addBed
          occupancy {
            adults
            extraBeds
          }
          cancellationPolicy {
            name
            description
            descriptionTranslatedList {
              label
              nativeLabel
              value
            }
            policies {
              applied
              priority
              penaltyAmount
              deadline
              deadlineTimeOffset
              penaltyType
              policyType
            }
          }
          modificationPolicy {
            name
            description
            descriptionTranslatedList {
              label
              nativeLabel
              value
            }
            policies {
              applied
              priority
              penaltyAmount
              deadline
              deadlineTimeOffset
              penaltyType
              policyType
            }
          }
          ratePolicy
          ratePolicyTranslatedList {
            label
            nativeLabel
            value
          }
          checkIn
          checkOut
          payablePrice
          originalPrice
          extraBedPrice
          boardType
          profiles {
            firstName
            lastName
            fullName
            identificationNo
            isForeigner
            email
            mobile
            phone
            id
          }
          roomTypeName
          roomTypeCode
          displayRoomTypeCode
          failedReason
          ratePlanName
          failureReasonCode
          technicalFailure {
            categoryCode
            providerCode
            httpStatus
            message
          }
          ratePlanCode
          displayRatePlanCode
          ratesPerDay {
            date
            roomRate
            extraBedRate
          }
        }
        description
        failedReason
      }
      total
    }
  }
`
